<template>
  <div class="relative">
    <ul
      ref="scrollableArea"
      class="flex gap-8 md:flex-row md:overflow-x-auto md:overflow-y-hidden lg:h-[var(--carousel-max-size)] lg:w-[var(--carousel-small-item-size)] lg:flex-col lg:overflow-y-auto lg:overflow-x-hidden lg:overscroll-y-auto"
      data-test="gallery-desktop-thumblist"
      data-testid="gallery-desktop-thumblist"
    >
      <slot name="default" />
    </ul>

    <Transition>
      <div
        v-if="shouldShowStartGradient"
        class="pointer-events-none absolute z-[2] hidden md:block md:left-0 md:top-0 md:h-full md:w-[52px] lg:inset-x-0 lg:top-0 lg:h-[52px] lg:w-auto"
        :class="[startGradientHorizontalClass, startGradientVerticalClass]"
        data-test="gallery-desktop-thumblist-gradient-start"
      />
    </Transition>

    <Transition>
      <div
        v-if="shouldShowEndGradient"
        class="pointer-events-none absolute z-[2] hidden md:block md:right-0 md:top-0 md:h-full md:w-[52px] lg:inset-x-0 lg:bottom-0 lg:top-auto lg:h-[52px] lg:w-auto"
        :class="[endGradientHorizontalClass, endGradientVerticalClass]"
        data-test="gallery-desktop-thumblist-gradient-end"
      />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, toRefs, useTemplateRef, watchEffect } from 'vue'

import { useEmitOnce } from '@backmarket/utils/composables/useEmitOnce'
import { useScroll } from '@vueuse/core'

const emit = defineEmits<{
  (e: 'scroll-start'): void
  (e: 'scroll-end'): void
}>()

const { emitOnce: emitOnceScrollStart } = useEmitOnce<['scroll-start']>(emit)
const { emitOnce: emitOnceScrollEnd } = useEmitOnce<['scroll-end']>(emit)

const scrollableArea = useTemplateRef('scrollableArea')
const { arrivedState, x, y } = useScroll(scrollableArea)
const { left, right, top, bottom } = toRefs(arrivedState)

const isScrollableHorizontally = computed(() => {
  void x.value

  return (
    (scrollableArea.value?.scrollWidth || 0) >
    (scrollableArea.value?.clientWidth || 0)
  )
})

const isScrollableVertically = computed(() => {
  void y.value

  return (
    (scrollableArea.value?.scrollHeight || 0) >
    (scrollableArea.value?.clientHeight || 0)
  )
})

const shouldShowStartGradient = computed(
  () =>
    (isScrollableHorizontally.value && !left.value) ||
    (isScrollableVertically.value && !top.value),
)

const shouldShowEndGradient = computed(
  () =>
    (isScrollableHorizontally.value && !right.value) ||
    (isScrollableVertically.value && !bottom.value),
)

// --- Scroll tracking (fire once via useEmitOnce) ---
watchEffect(() => {
  if (import.meta.server) return

  const scrolledAwayFromStart =
    (isScrollableHorizontally.value && !left.value) ||
    (isScrollableVertically.value && !top.value)

  const reachedEnd =
    (isScrollableHorizontally.value && right.value) ||
    (isScrollableVertically.value && bottom.value)

  if (scrolledAwayFromStart) {
    emitOnceScrollStart('scroll-start')
  }

  if (reachedEnd) {
    emitOnceScrollEnd('scroll-end')
  }
})

// md: horizontal gradients (left/right)
const startGradientHorizontalClass =
  'md:bg-[linear-gradient(90deg,#fff_0%,rgba(255,255,255,0)_100%)]'
const endGradientHorizontalClass =
  'md:bg-[linear-gradient(270deg,#fff_0%,rgba(255,255,255,0)_100%)]'

// lg: vertical gradients (top/bottom) — overrides md horizontal gradients
const startGradientVerticalClass =
  'lg:bg-[linear-gradient(180deg,#fff_0%,rgba(255,255,255,0)_100%)]'
const endGradientVerticalClass =
  'lg:bg-[linear-gradient(0deg,#fff_0%,rgba(255,255,255,0)_100%)]'
</script>
